import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import {
  FaBolt,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaHeadset,
  FaHeart,
  FaMapMarkerAlt,
  FaShieldAlt,
  FaStar,
  FaUsers,
  FaWallet,
  FaWifi,
  FaVideo,
  FaSnowflake,
  FaQrcode,
  FaRobot,
  FaSyncAlt,
  FaComments,
  FaHotel,
  FaCreditCard,
} from "react-icons/fa";
import { MdCompare, Md360 } from "react-icons/md";
import Navbar from "../../components/Navbar";
import LoginModal from "../../components/LoginModal";
import MeetingSpacePopup from "../../components/MeetingSpacePopup";
import { roomsAPI } from "../../services/meetingsAPI";
import { getImageUrl } from "../../utils/imageHelper";
import "react-datepicker/dist/react-datepicker.css";
import "./MeetingHomePremium.css";

const popularCities = ["Mumbai", "Bangalore", "Delhi", "Hyderabad", "Chennai", "Pune", "Kolkata"];

const featureItems = [
  { title: "Instant Booking", description: "Book your space in seconds.", icon: <FaBolt /> },
  { title: "Verified Spaces", description: "All spaces are quality checked.", icon: <FaShieldAlt /> },
  { title: "Best Price Guarantee", description: "No hidden charges, transparent pricing.", icon: <FaWallet /> },
  { title: "24/7 Support", description: "Round-the-clock assistance for every booking.", icon: <FaHeadset /> },
];

const quickTools = [
  { label: "Wishlist", icon: <FaHeart />, path: "/meetings/favourite" },
  { label: "Compare", icon: <MdCompare />, path: "/meetings/rooms" },
  { label: "360° Tour", icon: <Md360 />, path: "/meetings/rooms" },
  { label: "AI Recommendation", icon: <FaRobot />, action: "ai" },
  { label: "QR Check-in", icon: <FaQrcode />, path: "/meetings/my-bookings" },
];

const capacityOptions = [5, 10, 20, 50];
const amenityFilters = [
  { id: "wifi", label: "WiFi" },
  { id: "projector", label: "Projector" },
  { id: "ac", label: "AC" },
];

const fallbackRecommendedSpaces = [
  {
    id: "sample-1",
    roomName: "Elite Chamber",
    city: "Bangalore, Koramangala",
    pricePerHour: 2200,
    capacity: 10,
    rating: 4.8,
    reviews: 120,
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=900&q=80",
  },
  {
    id: "sample-2",
    roomName: "Training Room",
    city: "Mumbai, Andheri East",
    pricePerHour: 900,
    capacity: 18,
    rating: 4.6,
    reviews: 98,
    image: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=900&q=80",
  },
  {
    id: "sample-3",
    roomName: "Collaboration Suite",
    city: "Hyderabad, Hitech City",
    pricePerHour: 1100,
    capacity: 12,
    rating: 4.7,
    reviews: 105,
    image: "https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=900&q=80",
  },
  {
    id: "sample-4",
    roomName: "Conference Hall",
    city: "Delhi, Connaught Place",
    pricePerHour: 2000,
    capacity: 40,
    rating: 4.9,
    reviews: 150,
    image: "https://images.unsplash.com/photo-1517502884422-41eaead166d4?w=900&q=80",
  },
];

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80";

const MeetingHome = () => {
  const navigate = useNavigate();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [latestRooms, setLatestRooms] = useState([]);
  const [latestRoomsLoading, setLatestRoomsLoading] = useState(true);
  const [searchLocation, setSearchLocation] = useState("");
  const [searchDate, setSearchDate] = useState(null);
  const [searchTime, setSearchTime] = useState("");
  const [searchParticipants, setSearchParticipants] = useState("10");
  const [spaceType, setSpaceType] = useState("");
  const [priceMax, setPriceMax] = useState(5000);
  const [filterCapacity, setFilterCapacity] = useState(null);
  const [filterAmenities, setFilterAmenities] = useState([]);
  const [minRating, setMinRating] = useState("");
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [recentLocations, setRecentLocations] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("mh_recent_locations") || "[]");
    } catch {
      return [];
    }
  });

  const saveRecentLocation = (location) => {
    const normalized = (location || "").trim();
    if (!normalized) return;
    const updated = [normalized, ...recentLocations.filter((item) => item.toLowerCase() !== normalized.toLowerCase())].slice(0, 5);
    setRecentLocations(updated);
    localStorage.setItem("mh_recent_locations", JSON.stringify(updated));
  };

  const clearAdvancedFilters = () => {
    setSpaceType("");
    setPriceMax(5000);
    setFilterCapacity(null);
    setFilterAmenities([]);
    setMinRating("");
  };

  const handleQuickSearch = () => {
    const params = new URLSearchParams();
    if (searchLocation) {
      params.set("location", searchLocation);
      saveRecentLocation(searchLocation);
    }
    if (searchDate) params.set("date", format(searchDate, "yyyy-MM-dd"));
    if (searchTime) params.set("time", searchTime);
    const guests = filterCapacity || searchParticipants;
    if (guests) params.set("participants", String(guests));
    if (spaceType) params.set("spaceType", spaceType);
    if (priceMax < 5000) params.set("maxPrice", String(priceMax));
    if (minRating) params.set("minRating", minRating);
    filterAmenities.forEach((a) => params.append("amenity", a));
    navigate(`/meetings/search-results?${params.toString()}`);
  };

  const toggleAmenity = (id) => {
    setFilterAmenities((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSignInClick = () => {
    const existingRedirect = localStorage.getItem("authRedirectPath");
    if (!existingRedirect) {
      localStorage.setItem("authRedirectPath", "/meeting-home");
    }
    setIsLoginModalOpen(true);
  };

  useEffect(() => {
    let cancelled = false;
    setLatestRoomsLoading(true);

    const fetchLatestRooms = async () => {
      try {
        const response = await roomsAPI.getRooms({ sort: "latest" });
        const apiRooms = (response.data || [])
          .filter((room) => String(room?.visibility || "private").toLowerCase() === "public")
          .map((room) => ({
            id: room._id,
            _id: room._id,
            roomName: room.name || room.roomName || "Meeting Room",
            city: room.location?.city || room.city || "Unknown",
            capacity: room.capacity || 0,
            pricePerHour: room.pricePerHour ?? 0,
            images: Array.isArray(room.images) && room.images.length > 0 ? room.images.map((img) => getImageUrl(img)) : [],
            createdAt: room.createdAt ? new Date(room.createdAt).getTime() : 0,
          }));

        const byId = new Map();
        apiRooms.forEach((room) => {
          const key = room.id || room._id;
          if (!key) return;
          if (!byId.has(key) || (room.createdAt || 0) > (byId.get(key)?.createdAt || 0)) {
            byId.set(key, room);
          }
        });

        const sortedRooms = Array.from(byId.values()).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        if (!cancelled) setLatestRooms(sortedRooms.slice(0, 4));
      } catch (error) {
        console.error("Error fetching latest meeting rooms:", error);
        if (!cancelled) setLatestRooms([]);
      } finally {
        if (!cancelled) setLatestRoomsLoading(false);
      }
    };

    fetchLatestRooms();
    const refreshRooms = () => fetchLatestRooms();
    window.addEventListener("storage", refreshRooms);
    window.addEventListener("meetingRoomsUpdated", refreshRooms);

    return () => {
      cancelled = true;
      window.removeEventListener("storage", refreshRooms);
      window.removeEventListener("meetingRoomsUpdated", refreshRooms);
    };
  }, []);

  const spacesToRender = useMemo(() => {
    if (latestRooms.length === 0) return fallbackRecommendedSpaces;
    return latestRooms.map((room, index) => ({
      id: room.id || room._id || `room-${index}`,
      roomName: room.roomName || "Meeting Room",
      city: room.city || "Unknown",
      capacity: room.capacity || 0,
      pricePerHour: room.pricePerHour ?? 0,
      rating: Number((4.6 + (index % 4) * 0.1).toFixed(1)),
      reviews: 90 + index * 22,
      image: room.images?.[0] || fallbackRecommendedSpaces[index % 4]?.image,
      sourceId: room.id || room._id,
    }));
  }, [latestRooms]);

  const scrollToWidget = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleQuickTool = (tool) => {
    if (tool.action === "ai") {
      scrollToWidget("mhp-widget-ai");
      return;
    }
    if (tool.path) navigate(tool.path);
  };

  return (
    <div className="meeting-home-premium meeting-page-with-bottom-nav">
      <Navbar isScrolled={false} onSignInClick={handleSignInClick} showLoginModal />

      <main className="mhp-main">
        <div className="mhp-layout">
          <div className="mhp-left">
            <section className="mhp-hero">
              <div className="mhp-hero-grid">
                <div className="mhp-hero-left">
                  <span className="mhp-kicker">WORK. MEET. SUCCEED.</span>
                  <h1>
                    Find the perfect
                    <br />
                    <span>space for every meeting.</span>
                  </h1>
                  <p>
                    Book premium meeting rooms, conference halls, and event spaces with enterprise-grade
                    reliability and world-class hospitality.
                  </p>
                </div>
                <div className="mhp-hero-right">
                  <img src={HERO_IMAGE} alt="Modern conference room" />
                </div>
              </div>

              <div className="mhp-search-card">
                <div className="mhp-search-grid">
                  <div className="mhp-input">
                    <label htmlFor="meeting-location">
                      <FaMapMarkerAlt /> Location
                    </label>
                    <input
                      id="meeting-location"
                      type="text"
                      placeholder="Select location"
                      value={searchLocation}
                      onChange={(e) => {
                        setSearchLocation(e.target.value);
                        setShowLocationDropdown(true);
                      }}
                      onFocus={() => setShowLocationDropdown(true)}
                      onBlur={() => setTimeout(() => setShowLocationDropdown(false), 140)}
                    />
                    {showLocationDropdown && recentLocations.length > 0 && (
                      <div className="mhp-location-dropdown">
                        <div className="mhp-location-title">Recent searches</div>
                        {recentLocations.map((location) => (
                          <button
                            key={location}
                            type="button"
                            className="mhp-location-item"
                            onMouseDown={() => {
                              setSearchLocation(location);
                              setShowLocationDropdown(false);
                            }}
                          >
                            {location}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="mhp-input">
                    <label htmlFor="meeting-date">
                      <FaCalendarAlt /> Date
                    </label>
                    <DatePicker
                      id="meeting-date"
                      selected={searchDate}
                      onChange={(date) => setSearchDate(date)}
                      placeholderText="Select date"
                      dateFormat="dd-MM-yyyy"
                      minDate={new Date()}
                      className="mhp-date-input"
                      popperClassName="mhp-datepicker-popper"
                    />
                  </div>

                  <div className="mhp-input">
                    <label htmlFor="meeting-time">
                      <FaClock /> Time
                    </label>
                    <select id="meeting-time" value={searchTime} onChange={(e) => setSearchTime(e.target.value)}>
                      <option value="">Select time</option>
                      {["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00"].map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mhp-input">
                    <label htmlFor="meeting-guests">
                      <FaUsers /> People
                    </label>
                    <select
                      id="meeting-guests"
                      value={searchParticipants}
                      onChange={(e) => setSearchParticipants(e.target.value)}
                    >
                      {[1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30, 40, 50].map((count) => (
                        <option key={count} value={count}>
                          {count} Guest{count > 1 ? "s" : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button type="button" className="mhp-search-btn" onClick={handleQuickSearch}>
                    Search Spaces
                  </button>
                </div>

                <div className="mhp-advanced-filters">
                  <div className="mhp-filter-field">
                    <label>Space Type</label>
                    <select value={spaceType} onChange={(e) => setSpaceType(e.target.value)}>
                      <option value="">Any type</option>
                      <option value="conference">Conference Room</option>
                      <option value="boardroom">Boardroom</option>
                      <option value="training">Training Room</option>
                      <option value="event">Event Hall</option>
                    </select>
                  </div>
                  <div className="mhp-filter-field mhp-filter-field--range">
                    <label>Price Range (max ₹{priceMax.toLocaleString("en-IN")}/hr)</label>
                    <input
                      type="range"
                      min={500}
                      max={10000}
                      step={500}
                      value={priceMax}
                      onChange={(e) => setPriceMax(Number(e.target.value))}
                    />
                  </div>
                  <div className="mhp-filter-field">
                    <label>Capacity</label>
                    <div className="mhp-capacity-pills">
                      {capacityOptions.map((cap) => (
                        <button
                          key={cap}
                          type="button"
                          className={`mhp-cap-pill ${filterCapacity === cap ? "active" : ""}`}
                          onClick={() => setFilterCapacity(filterCapacity === cap ? null : cap)}
                        >
                          {cap === 50 ? "50+" : cap}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mhp-filter-field">
                    <label>Amenities</label>
                    <div className="mhp-amenity-checks">
                      {amenityFilters.map((a) => (
                        <label key={a.id} className="mhp-amenity-check">
                          <input
                            type="checkbox"
                            checked={filterAmenities.includes(a.id)}
                            onChange={() => toggleAmenity(a.id)}
                          />
                          {a.label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="mhp-filter-field">
                    <label>Rating</label>
                    <select value={minRating} onChange={(e) => setMinRating(e.target.value)}>
                      <option value="">Any rating</option>
                      <option value="4">4+ stars</option>
                      <option value="4.5">4.5+ stars</option>
                    </select>
                  </div>
                  <button type="button" className="mhp-more-filters" onClick={() => scrollToWidget("mhp-widget-filters")}>
                    More Filters
                  </button>
                  <button type="button" className="mhp-clear-filters" onClick={clearAdvancedFilters}>
                    Clear All
                  </button>
                </div>

                <div className="mhp-chip-row">
                  <span>Popular Searches:</span>
                  {popularCities.map((city) => (
                    <button key={city} type="button" className="mhp-chip" onClick={() => setSearchLocation(city)}>
                      {city}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            <section className="mhp-features">
              <h2>Why Choose MeetSpace?</h2>
              <div className="mhp-feature-grid">
                {featureItems.map((item) => (
                  <article className="mhp-feature-card" key={item.title}>
                    <div className="mhp-feature-icon">{item.icon}</div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </article>
                ))}
              </div>
            </section>

            <section className="mhp-spaces">
              <div className="mhp-section-head">
                <h2>Top Rated Spaces</h2>
                <button type="button" onClick={() => navigate("/meetings/rooms")}>
                  View all
                </button>
              </div>

              {latestRoomsLoading ? (
                <div className="mhp-status">Loading spaces...</div>
              ) : (
                <div className="mhp-space-scroll">
                  {spacesToRender.map((space) => (
                    <article
                      key={space.id}
                      className="mhp-space-card"
                      onClick={() => navigate(`/meetings/room/${space.sourceId || space.id}`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(`/meetings/room/${space.sourceId || space.id}`);
                        }
                      }}
                    >
                      <div className="mhp-space-image-wrap">
                        <img src={space.image} alt={space.roomName} />
                        <button
                          type="button"
                          className="mhp-heart"
                          onClick={(e) => e.stopPropagation()}
                          aria-label="Add to favorites"
                        >
                          <FaHeart />
                        </button>
                      </div>
                      <div className="mhp-space-body">
                        <h3>{space.roomName}</h3>
                        <p>
                          <FaMapMarkerAlt /> {space.city}
                        </p>
                        <div className="mhp-space-rating">
                          <span>
                            <FaStar /> {space.rating}
                          </span>
                          <small>{space.reviews} reviews</small>
                        </div>
                        <div className="mhp-space-amenities" aria-hidden>
                          <FaWifi title="WiFi" />
                          <FaVideo title="Projector" />
                          <FaSnowflake title="AC" />
                        </div>
                        <div className="mhp-space-foot">
                          <strong>INR {new Intl.NumberFormat("en-IN").format(space.pricePerHour)}</strong>
                          <span>/hour</span>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <nav className="mhp-quick-tools" aria-label="Quick features">
              {quickTools.map((tool) => (
                <button key={tool.label} type="button" className="mhp-quick-tool" onClick={() => handleQuickTool(tool)}>
                  <span className="mhp-quick-tool-icon">{tool.icon}</span>
                  <span>{tool.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <aside className="mhp-right" aria-label="Platform features">
            <article className="mhp-widget-card" id="mhp-widget-filters">
              <span className="mhp-widget-num">1</span>
              <h3>Smart Search &amp; Filters</h3>
              <p>Refine by price, capacity, amenities and availability in one place.</p>
              <div className="mhp-widget-demo mhp-widget-demo--filters">
                <div className="mhp-demo-slider">
                  <span>₹500</span>
                  <div className="mhp-demo-track" />
                  <span>₹10k</span>
                </div>
                <div className="mhp-capacity-pills mhp-capacity-pills--compact">
                  {capacityOptions.map((cap) => (
                    <span key={cap} className="mhp-cap-pill active">
                      {cap === 50 ? "50+" : cap}
                    </span>
                  ))}
                </div>
                <div className="mhp-amenity-checks">
                  {amenityFilters.map((a) => (
                    <span key={a.id} className="mhp-amenity-check">
                      ✓ {a.label}
                    </span>
                  ))}
                </div>
              </div>
            </article>

            <article className="mhp-widget-card">
              <span className="mhp-widget-num">2</span>
              <h3>Availability Calendar</h3>
              <div className="mhp-widget-demo mhp-widget-demo--calendar">
                <div className="mhp-cal-legend">
                  <span className="mhp-cal-dot mhp-cal-dot--green" /> Available
                  <span className="mhp-cal-dot mhp-cal-dot--yellow" /> Partial
                  <span className="mhp-cal-dot mhp-cal-dot--red" /> Booked
                </div>
                <div className="mhp-cal-grid">
                  {Array.from({ length: 14 }, (_, i) => (
                    <span
                      key={i}
                      className={`mhp-cal-day mhp-cal-day--${i % 3 === 0 ? "red" : i % 3 === 1 ? "yellow" : "green"}`}
                    >
                      {i + 10}
                    </span>
                  ))}
                </div>
                <ul className="mhp-slot-list">
                  <li><span className="mhp-slot mhp-slot--ok">10:00 AM</span> Available</li>
                  <li><span className="mhp-slot mhp-slot--warn">02:00 PM</span> Partial</li>
                  <li><span className="mhp-slot mhp-slot--busy">04:00 PM</span> Booked</li>
                </ul>
              </div>
            </article>

            <article className="mhp-widget-card">
              <span className="mhp-widget-num">3</span>
              <h3>Compare Rooms</h3>
              <table className="mhp-compare-table">
                <thead>
                  <tr>
                    <th />
                    <th>Room A</th>
                    <th>Room B</th>
                    <th>Room C</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Price/hr</td>
                    <td>₹1,200</td>
                    <td>₹900</td>
                    <td>₹2,200</td>
                  </tr>
                  <tr>
                    <td>Capacity</td>
                    <td>10</td>
                    <td>18</td>
                    <td>40</td>
                  </tr>
                  <tr>
                    <td>WiFi</td>
                    <td>✓</td>
                    <td>✓</td>
                    <td>✓</td>
                  </tr>
                </tbody>
              </table>
              <button type="button" className="mhp-widget-link" onClick={() => navigate("/meetings/rooms")}>
                Compare spaces
              </button>
            </article>

            <article className="mhp-widget-card">
              <span className="mhp-widget-num">4</span>
              <h3>360° Virtual Tour</h3>
              <div className="mhp-tour-preview">
                <img src={HERO_IMAGE} alt="360 tour preview" />
                <span className="mhp-tour-badge">360°</span>
              </div>
              <div className="mhp-tour-thumbs">
                {spacesToRender.slice(0, 3).map((s) => (
                  <img key={s.id} src={s.image} alt="" />
                ))}
              </div>
            </article>

            <article className="mhp-widget-card" id="mhp-widget-ai">
              <span className="mhp-widget-num">5</span>
              <h3>AI Room Recommendation</h3>
              <div className="mhp-ai-chat">
                <div className="mhp-ai-bubble mhp-ai-bubble--user">
                  Best meeting room for 15 people under ₹2000?
                </div>
                <div className="mhp-ai-bubble mhp-ai-bubble--bot">
                  Try <strong>Collaboration Suite</strong> in Hitech City — 12 seats, ₹1,100/hr with projector &amp; WiFi.
                </div>
              </div>
              <button type="button" className="mhp-widget-link" onClick={handleQuickSearch}>
                Search with AI hints
              </button>
            </article>

            <article className="mhp-widget-card">
              <span className="mhp-widget-num">6</span>
              <h3>QR Check-in</h3>
              <div className="mhp-qr-block">
                <div className="mhp-qr-placeholder" aria-hidden />
                <div>
                  <p><strong>Booking ID:</strong> MS-28491</p>
                  <p><strong>Date:</strong> {searchDate ? format(searchDate, "dd MMM yyyy") : "Today"}</p>
                  <p><strong>Time:</strong> {searchTime || "10:00 AM"}</p>
                </div>
              </div>
              <button type="button" className="mhp-widget-link" onClick={() => navigate("/meetings/my-bookings")}>
                My bookings
              </button>
            </article>

            <article className="mhp-widget-card mhp-widget-card--dashboard">
              <span className="mhp-widget-num">7</span>
              <h3>Dashboard Widgets</h3>
              <div className="mhp-dash-stats">
                <div><strong>2</strong><span>Today&apos;s Meetings</span></div>
                <div><strong>5</strong><span>Upcoming</span></div>
                <div><strong>48h</strong><span>Hours Booked</span></div>
                <div><strong>₹12k</strong><span>Monthly Spend</span></div>
              </div>
              <div className="mhp-upcoming-booking">
                <p className="mhp-upcoming-title">Product Strategy Sync</p>
                <span><FaCalendarAlt /> {searchDate ? format(searchDate, "dd MMM yyyy") : "Today"}</span>
                <span><FaClock /> {searchTime || "10:00 AM"} – 11:00 AM</span>
                <span><FaMapMarkerAlt /> Elite Chamber, Bangalore</span>
                <div className="mhp-widget-badge">
                  <FaCheckCircle /> Confirmed
                </div>
              </div>
              <button type="button" className="mhp-widget-btn" onClick={() => navigate("/meetings/book-flow")}>
                Quick Rebook
              </button>
            </article>

            <article className="mhp-widget-card">
              <span className="mhp-widget-num">8</span>
              <h3>Payment &amp; Invoicing</h3>
              <ul className="mhp-pay-options">
                <li><FaCreditCard /> Online Payment</li>
                <li><FaClock /> Pay Later</li>
                <li><FaUsers /> Corporate Billing</li>
              </ul>
              <div className="mhp-pay-summary">
                <span>Payment Summary</span>
                <strong>₹4,400</strong>
                <button type="button" onClick={() => navigate("/meetings/my-bookings")}>
                  Pay Now
                </button>
              </div>
            </article>

            <article className="mhp-widget-card">
              <span className="mhp-widget-num">9</span>
              <h3>Reviews &amp; Ratings</h3>
              <div className="mhp-reviews-head">
                <strong>4.8</strong>
                <div className="mhp-review-bars">
                  {[5, 4, 3, 2, 1].map((star) => (
                    <div key={star} className="mhp-review-bar">
                      <span>{star}★</span>
                      <div><i style={{ width: `${star * 18}%` }} /></div>
                    </div>
                  ))}
                </div>
              </div>
              <blockquote className="mhp-review-quote">
                &ldquo;Excellent space, seamless booking and great support team.&rdquo;
                <cite>— Priya S.</cite>
              </blockquote>
            </article>

            <article className="mhp-widget-card">
              <span className="mhp-widget-num">10</span>
              <h3>Extra Features</h3>
              <ul className="mhp-extra-list">
                <li><FaSyncAlt /> Recurring Booking</li>
                <li><FaClock /> Extend Booking Time</li>
                <li><FaHotel /> Nearby Hotels</li>
                <li><FaComments /> Live Chat Support</li>
              </ul>
              <button type="button" className="mhp-widget-link" onClick={() => navigate("/help")}>
                Get help
              </button>
            </article>
          </aside>
        </div>
      </main>

      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
      <MeetingSpacePopup />
    </div>
  );
};

export default MeetingHome;
